/**
 * ETP Expandable Text – "Show all" / "Show less" toggle.
 *
 * The collapsible half carries hidden="until-found", so find-in-page and
 * text-fragment links can still reach it while collapsed. Two ways in:
 *
 *   1. The button: clears/sets the attribute and toggles .is-expanded.
 *      Collapsing scrolls back to the top of the module so the reader does not
 *      end up below the content that just disappeared.
 *
 *   2. beforematch: the browser reveals a part on its own when a search hit
 *      lands inside it. We only sync button label and state afterwards, and
 *      open the remaining parts along with it.
 */

const SELECTOR = '.etp-expandable';

export function initExpandableText( scope ) {
  const root = scope || document;
  const roots = [ ...root.querySelectorAll( SELECTOR ) ];

  // Bricks re-renders pass the element itself as scope, in which case
  // querySelectorAll would miss it.
  if ( typeof root.matches === 'function' && root.matches( SELECTOR ) ) {
    roots.unshift( root );
  }

  roots.forEach( ( el ) => {
    if ( el.dataset.etpExpandableReady ) {
      return;
    }

    const toggle = el.querySelector( '.etp-expandable__toggle' );

    // The revealed content is either one block or, when both fields were lists
    // of the same type, the trailing <li> items of a single merged list.
    const parts = [ ...el.querySelectorAll( '.etp-expandable__more' ) ];

    // No second field means no button was rendered — nothing to wire up.
    if ( ! toggle || ! parts.length ) {
      return;
    }

    const setExpanded = ( expanded ) => {
      parts.forEach( ( part ) => {
        if ( expanded ) {
          part.removeAttribute( 'hidden' );
        } else {
          part.setAttribute( 'hidden', 'until-found' );
        }
      } );

      el.classList.toggle( 'is-expanded', expanded );
      toggle.setAttribute( 'aria-expanded', String( expanded ) );
      toggle.textContent = expanded
        ? ( toggle.dataset.less || 'Show less' )
        : ( toggle.dataset.more || 'Show all' );
    };

    toggle.addEventListener( 'click', () => {
      const willExpand = ! el.classList.contains( 'is-expanded' );
      setExpanded( willExpand );

      if ( ! willExpand ) {
        el.scrollIntoView( { behavior: 'smooth', block: 'start' } );
      }
    } );

    parts.forEach( ( part ) =>
      part.addEventListener( 'beforematch', () => setExpanded( true ) )
    );

    el.dataset.etpExpandableReady = 'true';
  } );
}
